/**
 * Validates if salary page has sufficient data for all components to render properly
 * Used to determine if page should be indexed by search engines
 */

interface Location {
  city: string | null
  state: string | null
}

interface SalaryDataValidationParams {
  sectionData: any
  roleParam: string | null
  location: Location
}

/**
 * Checks if salary data is insufficient for the current page configuration
 * Returns true if data is missing or insufficient (should noindex)
 * Returns false if all required data is present (should index)
 */
export function hasInsufficientSalaryData({
  sectionData,
  roleParam,
  location
}: SalaryDataValidationParams): boolean {
  if (!sectionData) return true

  const {
    salaryDetail,
    defaultData,
    locationData,
    citiesData,
    citiesRoleData,
    stateMapData
  } = sectionData

  // Check if we have the main salary detail (SalaryCard component)
  const hasSalaryDetail = salaryDetail?.success && salaryDetail?.data

  // Determine which filters are active
  const hasRole = Boolean(roleParam)
  const hasState = Boolean(location.state)
  const hasCity = Boolean(location.city)

  // Scenario 1: Role + State + City
  // Components: SalaryCard, CitiesInStateTable
  // Required: salaryDetail, citiesData
  if (hasRole && hasState && hasCity) {
    return !hasSalaryDetail || !citiesData?.success
  }

  // Scenario 2: Role + State (no city)
  // Components: SalaryCard, RoleSalaryTable
  // Required: salaryDetail, citiesData
  if (hasRole && hasState && !hasCity) {
    return !hasSalaryDetail || !citiesData?.success
  }

  // Scenario 3: Role only (no state/city)
  // Components: SalaryCard, CompanySalaryStateMap, RoleSalaryTable
  // Required: salaryDetail, stateMapData, citiesRoleData
  if (hasRole && !hasState && !hasCity) {
    return (
      !hasSalaryDetail
      || !stateMapData?.success
      || !citiesRoleData?.success
    )
  }

  // Scenario 4: State + City (no role)
  // Components: SalaryCard, CompanySalaryTable
  // Required: salaryDetail, locationData with actual records
  if (!hasRole && hasState && hasCity) {
    const hasLocationData
      = locationData?.success
        && locationData?.data
        && Array.isArray(locationData.data)
        && locationData.data.length > 0
    return !hasSalaryDetail || !hasLocationData
  }

  // Scenario 5: State only (no role/city)
  // Components: SalaryCard, CompanySalaryTable
  // Required: salaryDetail, locationData with actual records
  if (!hasRole && hasState && !hasCity) {
    const hasLocationData
      = locationData?.success
        && locationData?.data
        && Array.isArray(locationData.data)
        && locationData.data.length > 0
    return !hasSalaryDetail || !hasLocationData
  }

  // Scenario 6: No filters (default view)
  // Components: SalaryCard, CompanySalaryStateMap, CompanySalaryTable
  // Required: salaryDetail, defaultData with salaries, stateMapData
  if (!hasRole && !hasState && !hasCity) {
    const hasDefaultData
      = defaultData?.success
        && defaultData?.data
        && Array.isArray(defaultData.data.salaries)
        && defaultData.data.salaries.length > 0
    return !hasSalaryDetail || !hasDefaultData || !stateMapData?.success
  }

  // Fallback: if we reach here, consider data insufficient
  return true
}

/**
 * Validates if there's sufficient data for a specific salary component
 * Useful for conditional rendering in components
 */
export function hasDataForComponent(
  componentName: string,
  sectionData: any
): boolean {
  if (!sectionData) return false

  switch (componentName) {
    case 'SalaryCard':
      return Boolean(
        sectionData.salaryDetail?.success && sectionData.salaryDetail?.data
      )

    case 'CompanySalaryTable':
      return Boolean(
        (sectionData.defaultData?.success
          && sectionData.defaultData?.data?.salaries?.length > 0)
        || (sectionData.locationData?.success
          && sectionData.locationData?.data?.length > 0)
      )

    case 'CompanySalaryStateMap':
      return Boolean(
        sectionData.stateMapData?.success && sectionData.stateMapData?.data
      )

    case 'RoleSalaryTable':
      return Boolean(
        sectionData.citiesData?.success
        || sectionData.citiesRoleData?.success
      )

    case 'CitiesInStateTable':
      return Boolean(sectionData.citiesData?.success)

    default:
      return false
  }
}
